import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {HTMLUtil, Util} from 'yougile-util'
import {DataObjectId, Entity, Hub} from 'yougile-model'
import {Items} from '@api/model/items'
import {
  addingAllowed,
  deleteAllowed,
  renameAllowed
} from '@client/hub-actions-allowed'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'
import {BoardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {
  showAlert,
  showDeleteConfirm
} from '@components/common/base/notifications'
import {crmStore} from '@components/crm/crm-store'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {Board} from '@extensions/board'
import {globals} from '@extensions/globals-base'
import {Order} from '@extensions/order'
import {PageService} from '@extensions/page-service'
import {Project} from '@extensions/project'
import {UserAccess} from '@extensions/user-access'

type NewTasks = Map<DataObjectId, number>

interface State {
  boardIds: Set<DataObjectId>
  boardsOrder: Order
  boards: Hub[]
  boardsOrdered: Entity[]
  currentBoard?: Hub
  editedBoardId?: DataObjectId
  addingBoard: boolean
  newTasks: NewTasks
  movedBoardId?: DataObjectId
}

export class BoardTabsStore {
  state: State = {
    boardIds: new Set(),
    boardsOrder: new Order(globals.getCompanyId()),
    boards: [],
    boardsOrdered: [],
    currentBoard: undefined,
    editedBoardId: undefined,
    addingBoard: false,
    newTasks: new Map()
  }

  boardSettingsStore: BoardSettingsStore

  constructor(boardSettingsStore: BoardSettingsStore) {
    this.boardSettingsStore = boardSettingsStore

    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        boardSettingsStore: observable.shallow
      },
      {autoBind: true}
    )

    this.syncOnUpdate()
  }

  setCurrentBoard(boardId: DataObjectId, companyId: DataObjectId): void {
    if (Items.click(Items.get(boardId))) {
      PageService.goToBoard(boardId, companyId)
    }
  }

  syncOnUpdate(): void {
    this.syncRegularly()
  }

  syncRegularly(): void {
    const currentBoard = PageService.getCurrentBoard()
    this.state.currentBoard = currentBoard
    const newBoards = this.getProjectBoards()

    this.syncNewTasks(newBoards)
    this.syncBoards(newBoards, currentBoard)
  }

  private syncNewTasks(boards: Hub[]): void {
    const newTasks: NewTasks = new Map(this.state.newTasks.entries())

    boards.forEach(x => {
      const oldVal = newTasks.get(x.id) || 0
      const newVal = Board.getNewTasks(x).length
      if (oldVal !== newVal) {
        newTasks.set(x.id, newVal)
      }
    })

    this.state.newTasks = newTasks
  }

  private syncBoards(boards: Hub[], currentBoard: Hub): void {
    const project = PageService.getCurrentProject()
    if (!project) {
      return
    }
    const boardsOrder = Project.getBoardsOrder(project)
    this.state.boards = boards
    this.state.boardsOrder = boardsOrder
    this.updateBoardsOrdered(boards, boardsOrder)

    const oldBoardIds = this.state.boardIds
    if (
      oldBoardIds.size !== boards.length ||
      boards.find(b => !oldBoardIds.has(b.id))
    ) {
      const boardIds = new Set(boards.map(x => x.id))
      this.state.boardIds = boardIds
      if (currentBoard && !boardIds.has(currentBoard.id) && boardIds.size > 0) {
        PageService.goToProject(currentBoard.location, currentBoard.companyId)
      }
    }
  }

  private updateBoardsOrdered(boards: Hub[], boardsOrder: Order): void {
    // `Order` may contain "hidden" dot-prefixed entities, so we use an actual
    // list of to be displayed entities
    const orderMap = new Map(boards.map(x => [x.id, boardsOrder.order(x)]))
    this.state.boardsOrdered = boards.sort(
      (a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
    )
  }

  private getProjectBoards(): Hub[] {
    const project = PageService.getCurrentProject()
    if (!project) {
      return []
    }
    let res = Project.listBoards(project)
    res = Items.list(Items.fromHub(project), res.map(Items.fromHub)).map(
      Items.toHub
    )
    res = Util.removeDuplicateIds(res)
    return res
  }

  canEditBoardSettings(board: Hub): boolean {
    return PermissionsChecker.checkBoardSettings(board)
  }

  openBoardSettings(boardId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const board = box.getHub(boardId)
    if (!board || !PermissionsChecker.checkBoardSettings(board)) {
      return
    }

    this.boardSettingsStore.openForBoard(boardId, 'boardSettings')
    // scope.filter.stopEdit()
  }

  canShareBoard(board: Hub): boolean {
    if (PermissionsChecker.isAdmin()) {
      return true
    }
    const box = Sync.getCurrentSandbox()
    const proj = box.getProjectFor(board)
    const role = UserAccess.getRoleInProject(globals.currentUserId, proj)
    return role === 'admin'
  }

  openBoardSharing(boardId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const board = box.getHub(boardId)
    if (!board || !this.canShareBoard(board)) {
      return
    }

    this.boardSettingsStore.openForBoard(boardId, 'boardSharing')
    // scope.filter.stopEdit()
  }

  canDeleteBoard(board: Hub): boolean {
    return deleteAllowed(board.id)
  }

  async deleteBoard(boardId: DataObjectId): Promise<void> {
    const box = Sync.getCurrentSandbox()
    const board = box.getHub(boardId)
    if (!board) {
      return
    }

    if (!Items.delete(Items.fromHub(board))) {
      if (board.deleted) {
        PageService.goToBoard(this.state.boardsOrder.getList()[0] as Hub)
      }
      // scope.filter.stopEdit()
      return
    }

    if (!PermissionsChecker.checkDeletion(board)) {
      MessageBox.warn(Lang.msg.permissionsForbiddenBoardDelete)
      // scope.filter.stopEdit()
      return
    }

    if (Project.listBoards(PageService.getCurrentProject()).length < 2) {
      await showAlert(Lang.msg.noDeleteLastBoard)
      return
    }

    if (this.state.currentBoard) {
      if (await showDeleteConfirm(Lang.msg.confirmBoardDelete)) {
        Project.removeBoard(board)
        // scope.filter.stopEdit()
        if (this.state.editedBoardId === this.state.currentBoard.id) {
          PageService.goToBoard(this.state.boardsOrder.getList()[0] as Hub)
        }
      }
    }
  }

  canAddBoard(board: Hub): boolean {
    return addingAllowed(board?.location)
  }

  canAddBoardToCurrentProject(): boolean {
    const project = PageService.getCurrentProject()
    return addingAllowed(project?.id)
  }

  startBoardAdding(): void {
    this.state.addingBoard = true
  }

  addNewBoard(title?: string): void {
    this.state.addingBoard = false

    title = title ? HTMLUtil.trimLineBreaks(title) : ''

    if (!title) {
      return
    }

    const project = PageService.getCurrentProject()
    const isCrmProject =
      project.data?.projectType === 'crm' && crmStore.isCrmEnabled

    const withDefaults = !isCrmProject
    const board = Project.addBoard(title, project, withDefaults, true)

    if (!board) {
      return
    }

    void Tracking.send(TrackingEvent.boardCreate)
    this.setCurrentBoard(board.id, board.companyId)

    if (isCrmProject) {
      const box = Sync.getCurrentSandbox()
      Board.createCrmTemplateColumns(box, board)
      void Tracking.send(TrackingEvent.crmBoardCreate)
    }
  }

  duplicateBoard(boardId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(boardId)
    hub && Board.duplicate(hub)
  }

  canMoveBoard(board: Hub): boolean {
    return globals.dragAllowed(board.id)
  }

  openBoardMove(boardId: DataObjectId): void {
    this.state.movedBoardId = boardId
  }

  closeBoardMove(): void {
    this.state.movedBoardId = undefined
  }

  moveBoard(projects: [Hub]): void {
    const boardId = this.state.movedBoardId
    this.state.movedBoardId = undefined

    const box = Sync.getCurrentSandbox()
    const board = box.getHub(boardId)
    if (!board || projects.length < 1 || projects[0].id === board.location) {
      return
    }

    const to = projects[0]
    const from = box.get(board.location) as Hub

    const isAllowed =
      Items.move(
        Items.fromHub(board),
        Items.fromHub(from),
        Items.fromHub(to)
      ) && PermissionsChecker.checkMove(board, from, to)
    if (!isAllowed) {
      MessageBox.warn(Lang.msg.permissionsForbiddenMove)
      return
    }

    box.moveTreeNode(board, from.id, to.id, true, false)
    const oldOrder = Project.getBoardsOrder(from)
    oldOrder.removeEntity(board)
    oldOrder.checkAndFixEntities()
    const order = Project.getBoardsOrder(to)
    order.handleDrop(0, board)
  }

  canRenameBoard(board: Hub): boolean {
    return renameAllowed(board.id)
  }

  startBoardRenaming(boardId: DataObjectId): void {
    this.state.editedBoardId = boardId
  }

  renameEditedBoard(title?: string): void {
    const boardId = this.state.editedBoardId
    this.state.editedBoardId = undefined

    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(boardId)
    title = title ? HTMLUtil.trimLineBreaks(title) : ''
    if (
      hub &&
      title &&
      hub.title !== title &&
      Items.rename(Items.fromHub(hub), title)
    ) {
      box.editTitle(hub, title)
    }
  }

  dropBoard(droppedBoard: Hub, ontoBoard: Hub, after?: boolean): void {
    after
      ? this.state.boardsOrder.insertAfter(droppedBoard, ontoBoard)
      : this.state.boardsOrder.insertBefore(droppedBoard, ontoBoard)
    this.updateBoardsOrdered(this.state.boards, this.state.boardsOrder)
  }
}

export const [BoardTabsContext, useBoardTabsStore] =
  createUseStore(BoardTabsStore)
